import { TFunction } from "i18next";
import { SelectOption } from "../components/Select";
import { TreatmentPlanResponse } from "../services/treatmentPlanService";

export function formatTreatmentPlanDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

export function formatTreatmentPlanTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function getTreatmentPlanStatusLabel(
  status: number,
  t: TFunction,
): string {
  switch (status) {
    case -1:
    case 0:
      return t("treatmentPlan.status.generating");
    case 1:
      return t("treatmentPlan.status.aiDrafted");
    case 2:
      return t("treatmentPlan.status.awaitingProviderReview");
    case 3:
      return t("treatmentPlan.status.awaitingPatientConfirmation");
    case 4:
      return t("treatmentPlan.status.declinedByPatient");
    case 5:
      return t("treatmentPlan.status.approvedByPatient");
    case 6:
      return t("treatmentPlan.status.inProgress");
    case 7:
      return t("treatmentPlan.status.completed");
    case 8:
      return t("treatmentPlan.status.generationFailed");
    default:
      return t("treatmentPlan.status.unknown");
  }
}

export function getTreatmentPlanStatusType(status: number): string {
  switch (status) {
    case -1:
    case 0:
    case 1:
    case 2:
      return "pending";
    case 3:
      return "in-progress";
    case 4:
      return "declined-by-patient";
    case 5:
    case 6:
      return "approved";
    case 7:
      return "completed";
    case 8:
      return "error";
    default:
      return "pending";
  }
}

export function getTreatmentPlanPatientName(
  treatmentPlan: TreatmentPlanResponse,
): string {
  if (treatmentPlan.patientFirstName || treatmentPlan.patientLastName) {
    return `${treatmentPlan.patientFirstName || ""} ${treatmentPlan.patientLastName || ""}`.trim();
  }
  return `Patient #${treatmentPlan.patientUid}`;
}

export function getTreatmentPlanProviderName(
  treatmentPlan: TreatmentPlanResponse,
): string {
  if (treatmentPlan.providerFirstName || treatmentPlan.providerLastName) {
    return `${treatmentPlan.providerFirstName || ""} ${treatmentPlan.providerLastName || ""}`.trim();
  }
  return `Provider #${treatmentPlan.providerId}`;
}

export interface TreatmentPlanMetaItem {
  text: string;
  key: string;
}

export function buildTreatmentPlanMetaItems(
  treatmentPlan: TreatmentPlanResponse,
  t: TFunction,
): TreatmentPlanMetaItem[] {
  const metaItems: TreatmentPlanMetaItem[] = [
    {
      text: formatTreatmentPlanDate(treatmentPlan.dateGenerated),
      key: "date",
    },
    {
      text: formatTreatmentPlanTime(treatmentPlan.dateGenerated),
      key: "time",
    },
  ];

  const patientName = getTreatmentPlanPatientName(treatmentPlan);

  if (treatmentPlan.status === 5 && treatmentPlan.approvedByPatientAt) {
    metaItems.push({
      text: t("treatmentPlan.approvedByPatient", {
        name: patientName,
        date: formatTreatmentPlanDate(treatmentPlan.approvedByPatientAt),
        time: formatTreatmentPlanTime(treatmentPlan.approvedByPatientAt),
      }),
      key: "approvedByPatient",
    });
  } else if (treatmentPlan.approvedByProviderAt) {
    metaItems.push({
      text: t("treatmentPlan.approvedOn", {
        date: formatTreatmentPlanDate(treatmentPlan.approvedByProviderAt),
      }),
      key: "approved",
    });
  }

  return metaItems;
}

export function isReApprovalStatus(status: number): boolean {
  return [3, 4, 5, 6].includes(status);
}

export function resolveTreatmentPlanSaveStatus(
  currentStatus: number,
  options: { reApprove?: boolean; explicitStatus?: number } = {},
): number {
  if (options.explicitStatus !== undefined) return options.explicitStatus;
  if (options.reApprove && isReApprovalStatus(currentStatus)) return 3;
  if (currentStatus === 1) return 2;
  return currentStatus;
}

export function getCanEditTreatmentPlan(
  isProvider: boolean,
  status: number,
): boolean {
  return (
    isProvider &&
    status !== 7 &&
    status !== 8 &&
    status !== 0 &&
    status !== -1
  );
}

/** @deprecated Use getCanEditTreatmentPlan */
export const getCanEditTreatmentPlanHeader = getCanEditTreatmentPlan;

export function getCanEditTreatmentPlanHeaderStatus(status: number): boolean {
  return status === 5 || status === 6;
}

export function getTreatmentPlanHeaderStatusOptions(
  status: number,
  t: TFunction,
): SelectOption[] {
  if (status === 5) {
    return [{ value: 6, label: t("treatmentPlan.status.inProgress") }];
  }
  if (status === 6) {
    return [{ value: 7, label: t("treatmentPlan.status.completed") }];
  }
  return [];
}
